import { ReactNode } from 'react'
import {
  CARD_REQUIRED_ERROR_CODE,
  CREDITS_EXHAUSTED_ERROR_CODE,
  describeTurnError,
  ONBOARDING_REQUIRED_ERROR_CODE,
  RAY_ID_UNAVAILABLE,
  SUBSCRIPTION_REQUIRED_ERROR_CODE,
  type ChatTurnError,
} from '../engine/errors'
import { ChatAttachment, type FinalizedAttachment, type PendingAttachmentStagedAction } from './attachments'
import { UserImage } from './shortcuts'
import { components } from './backend-openapi'

export const AUDIO_NOTE_PLANNER_KICKOFF_KIND = 'audio-note-planner-kickoff'
/** Matches the gateway constant used to replace a resume prompt with its Q/A summary. */
export const ASK_USER_ANSWER_PRESENTATION_KIND = 'ask-user-answer'

export type UserMessagePresentation = {
  kind: string
  skill?: string
  toolset?: string
  stagedActionId?: string
  label?: string
}

export class ChatError extends Error {
  chatId: string
  userMessageId: string | null
  assistantMessageId: string | null
  errorCode: string | null
  rayId?: string
  /**
   * Typed taxonomy value. Attached at the failure site so the
   * throw-based flow keeps full fidelity; `toChatTurnError` unwraps it in
   * preference to re-deriving from the legacy string fields.
   */
  turnError?: ChatTurnError
  constructor(
    message: string,
    {
      chatId,
      userMessageId,
      assistantMessageId,
      errorCode,
      rayId,
      turnError,
    }: {
      chatId: string
      userMessageId: string | null
      assistantMessageId: string | null
      errorCode: string | null
      rayId?: string
      turnError?: ChatTurnError
    },
  ) {
    super(message)
    this.chatId = chatId
    this.userMessageId = userMessageId
    this.assistantMessageId = assistantMessageId
    this.errorCode = errorCode
    this.rayId = rayId ?? RAY_ID_UNAVAILABLE
    this.turnError = turnError
  }

  /**
   * The one bridge from the typed taxonomy to the legacy throw channel:
   * every legacy field is a projection of the taxonomy value, so throw sites
   * can't drift from it. Message ids default to null (no specific message);
   * pass `ids` only when the failure should attach to one.
   */
  static fromTurnError(
    turnError: ChatTurnError,
    ids?: { userMessageId?: string | null; assistantMessageId?: string | null },
  ): ChatError {
    return new ChatError(describeTurnError(turnError), {
      chatId: turnError.chatId,
      userMessageId: ids?.userMessageId ?? null,
      assistantMessageId: ids?.assistantMessageId ?? null,
      errorCode: legacyErrorCode(turnError),
      rayId: 'rayId' in turnError ? turnError.rayId : undefined,
      turnError,
    })
  }
}

/** The legacy `errorCode` a taxonomy value projects to; null for members the legacy channel never coded. */
function legacyErrorCode(turnError: ChatTurnError): string | null {
  switch (turnError._tag) {
    case 'BackendTurnError':
      return turnError.errorCode
    case 'AttachmentUploadError':
      return 'attachment-upload-error'
    case 'CreditsExhausted':
      return CREDITS_EXHAUSTED_ERROR_CODE
    case 'SubscriptionRequired':
      return SUBSCRIPTION_REQUIRED_ERROR_CODE
    case 'CardRequired':
      return CARD_REQUIRED_ERROR_CODE
    case 'OnboardingRequired':
      return ONBOARDING_REQUIRED_ERROR_CODE
    default:
      return null
  }
}

type WindowAttachment = {
  type: 'window'
  title: string
  appIcon?: string
}

export type BaseMessage = {
  id: string
  /**
   * Client-minted identity that survives the optimistic→confirmed id swap and
   * the temp→real chat-id swap. Set on optimistic messages at creation; the
   * backend id lands in `id` alongside it. Used for stable group/React keys.
   * `clientId && !id` identifies specifically the unadopted assistant
   * placeholder — optimistic user messages mint `id` = `clientId` up front,
   * so they are never id-less.
   */
  clientId?: string
  conversation_id: string
  role: 'user' | 'assistant' | 'tool'
  version?: string
  content?: string | ReactNode
  given_feedback?: string | null
  error?: {
    // This is only ever set by the client
    /** The failed turn's `ChatTurnError` tag, so the error UI reads this message's failure, not the chat's latest. */
    kind?: ChatTurnError['_tag']
    canRetry?: boolean
    message?: string
    rayId?: string
  }
}

export type UserMessage = BaseMessage & {
  role: 'user'
  metadata?: {
    presentation?: UserMessagePresentation
  }
  context?: string
  image_url?: string
  ocr_text?: string
  clipboard_text?: string
  screenshot?: string
  audio?: string
  window?: WindowAttachment
  file_title?: string
  windows?: string[]
  file_attachments?: Attachment[]
  window_context?: string
  factIndex?: number
  fact?: string
  attached_context?: FinalizedAttachment[]
  given_feedback?: string | null
  mentions: MentionableItem[]
  model_id?: string
  available_context?: string[]
  window_scope?: string
  push_to_talk_mode?: string
  additional_tools?: components['schemas']['ToolDefinition'][]
  pending_context?: ChatAttachment[] // client-side only
}

export type ToolCallMetadata = {
  parentToolId?: string
  rootToolId?: string
  depth?: number
}

export type ToolSubstep = {
  stepId: string
  parentStepId?: string
  stepName: string
  stepStatus: 'started' | 'streaming' | 'completed'
  details?: string
  content?: string
}

export type ContentPart = { type: 'text'; text: string } | { type: 'tool_call'; toolCallId: string }

export type SubagentState = {
  index: number
  messageId: string
  parentToolCallId: string
  content: string
  thinkingContent: string
  isThinkingStreaming: boolean
  status: 'streaming' | 'completed'
}

export type ToolCall = {
  id: string
  toolName: string
  toolInput: object
  toolType: 'builtin' | 'failure'
  status: 'ready' | 'executing' | 'success' | 'failed' | 'cancelled'
  result?: string
  error?: string
  autoRun?: boolean
  metadata?: ToolCallMetadata
  steps?: ToolSubstep[]
  subagents?: Array<SubagentState>
  view?: {
    name: string
    icons: Array<string>
    description: string
  }
}

export type AssistantMessage = BaseMessage & {
  role: 'assistant'
  conversation_id: string
  id: string | null
  given_feedback: string | null
  factIndex?: number
  fact?: string
  visualization?: VisualizationData | undefined
  useAdvancedReasoning?: boolean
  model_id?: string
  toolCalls?: ToolCall[]
  contentParts?: ContentPart[]
  thinkingContent?: string
  isThinkingStreaming?: boolean
  /**
   * Raw accumulated stream text, stamped by the engine fold only when it
   * differs from `content` (inline `<think>` markup present). Stream resume
   * seeds the accumulator from this so replayed deltas extend text whose
   * think markers are still intact (engine/accumulator.ts).
   */
  rawContent?: string
}

export type ToolMessage = BaseMessage & {
  role: 'tool'
  toolCallId: string
  name?: string
  metadata?: ToolCallMetadata
  steps?: ToolSubstep[]
}

export type ChatMessage = UserMessage | AssistantMessage | ToolMessage

export interface ImageAttachment {
  type: 'image'
  value: string
  file?: File
}

export interface PdfAttachment {
  type: 'pdf'
  value: File
}

export interface AudioAttachment {
  type: 'audio'
  value: string
  duration: number // in minutes
}

export interface ClipboardAttachment {
  type: 'clipboard'
  value: string
}

export interface SelectedTextAttachment {
  type: 'selected_text'
  value: string
}

export interface TextFileAttachment {
  type: 'text_file'
  value: string
  fileName: string
}
export interface WindowContextAttachment {
  type: 'window_context'
  value: string
}

export interface SpreadsheetAttachment {
  type: 'spreadsheet'
  value: File
}

export interface ConversationAttachment {
  id: string
  type: 'conversation'
  value: string
  title: string
  startedAt: Date
  endedAt: Date
  isCurrentConversation?: boolean
}

export interface QuoteReferenceAttachment {
  type: 'quoteReference'
  value: string
}

export type FileAttachment =
  | PdfAttachment
  | ImageAttachment
  | SpreadsheetAttachment
  | TextFileAttachment
  | WindowContextAttachment

export type Attachment =
  | AudioAttachment
  | ClipboardAttachment
  | SelectedTextAttachment
  | WindowAttachment
  | FileAttachment
  | WindowContextAttachment
  | ConversationAttachment
  | QuoteReferenceAttachment

export type ChatHistoryItem = components['schemas']['Conversation']

export interface ModalObjectProps {
  id: string
  context?: Record<string, any>
}

export type Prompt = {
  name: string
  slug: string
  image: string | null
  user_id: string
  created_at: string
  description: string
  external_id: string
  prompt_text: string
  user_images: {
    file_extension: string
  } | null
  last_usage: string | null
  public_use_number?: number
  preferred_attachment: 'default' | 'screen' | 'page-text' | 'clipboard' | 'audio' | null | undefined
}

export type MetadataEvent = {
  type: 'metadata'
  conversation_id: string
  message_id: string
  model?: string
  llm_provider?: string
  has_live_data?: boolean
  requires_live_data?: string
  tool_id?: string
  provider_switch?: boolean
  switch_reason?: string
  from_provider?: string
  to_provider?: string
  provider?: string
  visualization?: boolean
  search?: boolean
  knowledge_used?: boolean
  favicons?: string[]
  audio_notes_search?: boolean
}

export interface VisualizationData {
  iframe: string
  description: string
  title: string
  url: string
}

export type SendMessageOptions = {
  isNewChat?: boolean
  attachments?: ChatAttachment[]
  // If provided, these are already finalized and will be sent as-is
  attachedContext?: FinalizedAttachment[]
  isPushToTalk?: boolean
  pushToTalkMode?: string
  prompt?: PromptShortcut
  toolOverrides?: Record<string, { enabled: boolean; tools: string[] | '*' }>
  requestCurrentContext?: boolean // This will *request* current context, cannot be used with below
  waitForContext?: boolean // This will wait for any pending context requests
  mentions?: MentionableItem[]
  useAdvancedReasoning?: boolean
  isOnboardingChat?: boolean
  useKnowledge?: boolean
  ephemeral?: boolean
  conversationId?: string
  onSubmit?: (conversationId: string) => void
  stagedActionAttachment?: PendingAttachmentStagedAction
  /**
   * Don't touch the chat composer. Set by programmatic submits that consume no
   * composer text (e.g. resuming a turn after an AskUserQuestion tool-result)
   * so a draft the user is typing isn't cleared — or clobbered by the
   * failure-rollback restore.
   */
  preserveDraft?: boolean
  /**
   * Don't consume or clear the shared composer's attachments and mentions.
   * Set by programmatic submits rendered outside the main chat composer.
   */
  preserveComposerContext?: boolean
  /**
   * Don't make this turn's conversation the globally-active chat. Set by
   * background turns the user never sees as a chat (staged-action inline
   * edits): claiming activeChatId would make every activeChatId-keyed surface
   * — and invoke's "is the user viewing this chat" resume check — treat the
   * hidden edit conversation as the one currently open.
   */
  preserveActiveChat?: boolean
  /** Don't claim the pending message (the one Retry resubmits): a background turn must not take the visible chat's Retry. */
  preservePendingMessage?: boolean
  /** Reuse the ChatObject queue id after a transport-uncertain failure. */
  resendMsgId?: string
  /** Skill compare (dev only): run this turn as one side of a live pair. */
  skillCompare?: { pairId: string; side: 'old' | 'new' }
  /**
   * Open this new chat under the conversation id the composer already had the
   * backend prepare (see `hooks/turn-preparation.ts`), so the turn starts on
   * the conversation that is ready for it. Set by the composer only: the id
   * is handed out once, and other new-chat callers must not take it.
   */
  usePreparedConversation?: boolean
  /**
   * Open this new chat under an id the caller chose, such as the one a meeting
   * note names for its thread. Wins over `usePreparedConversation`.
   */
  newConversationId?: string
  /**
   * The sandbox a new chat's attached files were granted under, which names the chat. Set on the pending message so
   * Retry opens the chat where those files are, since the composer handed the id out to the failed attempt.
   */
  newChatSandboxId?: string
}

export type PromptShortcut = {
  name: string
  slug: string
  image: string | null
  userId: string
  createdAt: string
  description: string
  externalId: string
  promptText: string
  userImages: {
    fileExtension: string
  } | null
  lastUsage: string | null
  preferredAttachment: 'default' | 'screen' | 'page-text' | 'clipboard' | 'audio' | null | undefined
  scope: {
    applicationNameDarwin: string[]
    applicationNameWin32: string[]
    contextTypes: {
      screenshot: boolean
      selectedText: boolean
      clipboardText: boolean
      audioTranscription: boolean
      window: boolean
    }
  }
  agentTools: {
    [key: string]: {
      enabled: boolean
      tools: '*' | string[]
    }
  } | null
}

export type Shortcut = {
  id: string
  createdAt: string
  userId: string
  name: string
  slug: string
  imageUrl: string | null
  description: string
  prompt: string
  uses: number
  public: boolean
}

export type SkillType = 'prompt' | 'workflow' | 'toolset_instructions'

export interface SkillExamples {
  good?: Array<string>
  bad?: Array<string>
}

export interface SkillSchedule {
  cron: string
  requireActiveUser: boolean
  timezone?: string
  /** Also DM what each scheduled run delivers to the user in Slack. Omitting it keeps the current choice. */
  deliverToSlack?: boolean
}

/**
 * Per-surface availability toggles. Each boolean independently gates one
 * surface the skill can appear on; all default to `true` on the backend.
 *
 * - `audioPlanner` — staged-actions / audio-note planner candidate
 * - `agents` — chat agent auto-selection
 * - `userInvoke` — slash-command / @mention picker and direct invocation
 */
export interface SkillAvailability {
  audioPlanner: boolean
  agents: boolean
  userInvoke: boolean
}

/**
 * Webhook event triggers, keyed by provider (e.g. `linear`, `slack`). Each
 * value is the list of provider-namespaced event types the skill should fire
 * on (e.g. `Issue.create`). Auto-detected from skill content on save unless
 * `triggerEventsUserEdited` is true. Render against the catalog returned by
 * `GET /api/v1/skills/event-catalog` to surface friendly labels.
 */
export type SkillTriggerEvents = Record<string, Array<string>>

/**
 * Backend-authoritative onboarding-picker presentation. Populated only on
 * default skills that appear in onboarding (see `SkillSchema.onboarding`);
 * null/absent otherwise. Lets us update onboarding copy with a backend deploy
 * instead of an app release.
 *
 * Mirrors the gateway's `SkillOnboarding` OpenAPI ref. TODO: once the branch's
 * schema is deployed, regenerate `backend-openapi.d.ts` (`pnpm gen:api-types`)
 * and replace this with `components['schemas']['SkillOnboarding']` — the
 * generated file predates the field, so it can't be derived yet.
 */
export interface SkillOnboarding {
  /** Every field is an optional overlay — the client falls back to the skill's
   * own name / description / metadata icon per-field. */
  displayName?: string
  subtitle?: string
  iconUrl?: string
  /** Higher values appear first in onboarding recommendations. */
  priority?: number
  /**
   * Ordered provider ids matching `GET /api/v1/connections/providers` — resolve
   * name/icon/connect flow from that endpoint. May be empty (no connect step).
   */
  integrations?: Array<string>
}

/**
 * Per-event-type relevance scope — *whose* events wake a skill, narrowing the
 * *which* answered by {@link SkillTriggerEvents}. `any` (the default for any
 * absent entry) fires on every matching event; the others restrict to events
 * about the user:
 *   - `assigned_to_me` — the entity is assigned to the user (e.g. Linear issues)
 *   - `mentions_me` — the user is @-mentioned
 *   - `authored_by_me` — the user is the actor/author
 *   - `not_authored_by_me` — someone *other* than the user acted
 */
export type SkillRelevanceScope = 'any' | 'assigned_to_me' | 'mentions_me' | 'authored_by_me' | 'not_authored_by_me'

/**
 * Relevance scopes parallel to {@link SkillTriggerEvents}: provider → event
 * type → scope (e.g. `{ linear: { 'Issue.create': 'assigned_to_me' } }`).
 * Absent entries default to `any`. Auto-detected alongside `triggerEvents`.
 *
 * Must be re-sent whenever a manual `triggerEvents` override is saved — the
 * backend resets scopes to all-`any` on any override that omits them, which
 * would silently widen *whose* events fire.
 */
export type SkillTriggerEventScopes = Record<string, Record<string, SkillRelevanceScope>>

export interface SkillPreference {
  id: string
  content: string
  /**
   * `user` is what the user wrote and `agent` what Highlight inferred from
   * feedback. Any other slug labels an entry a skill keeps for itself, so it
   * can rewrite its own entry — those still render and edit like any other.
   */
  source: string
  /**
   * Provenance the server stamped at the point of the write — the chat it
   * happened in, or the pass that produced it. Read by Settings to attribute
   * the entry; null for entries written before it was recorded.
   */
  metadata?: Record<string, unknown> | null
  createdAt: string
  /**
   * When the entry last changed. Equal to `createdAt` until someone edits it,
   * and the date Settings shows — an entry rewritten last week is current as
   * of last week, not as of the run that first proposed it.
   */
  updatedAt: string
}

export interface Skill {
  id: string
  createdAt: string
  userId: string
  name: string
  slug: string
  description: string
  /**
   * User-facing Markdown blurb authored on Highlight's catalog definitions,
   * written for a person rather than for the planner and agent skills block
   * that route on `description`. It is absent on user-created and team skills,
   * so fall back to `description` wherever a blurb is shown.
   */
  displayDescription?: string | null
  prompt: string
  skillContent: string | null
  /** Per-skill preferences, one entry each. Editing them never forks a default. */
  preferences: Array<SkillPreference>
  toolset: string | null
  skillType: SkillType
  isDefault: boolean
  /**
   * Which skill catalog this default came from: an onboarding role name for
   * persona catalogs, `general` for the seed fallback catalog, `internal` for
   * the internal add-on catalog, or `default` for the pre-seed catalog. Null
   * for DB rows (user-owned skills and legacy
   * defaults) — a fork inherits its parent default's catalog via
   * `forkedFromId`. Drives the Settings catalog grouping.
   */
  catalog?: string | null
  forkedFromId: string | null
  examples: SkillExamples | null
  version: number
  metadata: Record<string, unknown> | null
  imageUrl?: string
  public: boolean
  /** Organization containing this published snapshot; null for personal and default skills. */
  organizationId?: string | null
  /** User who published this organization snapshot; null for personal and default skills. */
  publishedBy?: string | null
  publications: Array<{ organizationId: string; upToDate: boolean }>
  uses: number
  availability: SkillAvailability
  schedule?: SkillSchedule | null
  nextRunAt?: string | null
  disabledForUser?: boolean
  /** Backend-resolved entitlement state for this request's user and plan. */
  planLocked?: boolean
  /** Scheduled runs can also DM what they deliver to the user in Slack. */
  slackDelivery?: boolean
  /** Minimum plan required to use this skill. */
  tier?: 'lite' | 'pro' | 'max'
  folder: string
  /**
   * Curated audience labels (canonical onboarding roles — see backend
   * `GET /v1/roles`). Empty or containing the `general` marker means
   * audience-unconstrained (shown to everyone). Drives the onboarding
   * Recommended/Also-available split.
   */
  roles?: Array<string>
  /** Free-form grouping tags (e.g. `meetings`). Not a curated axis. */
  tags?: Array<string>
  /** Backend-authoritative onboarding-picker presentation; null/absent off the onboarding path. */
  onboarding?: SkillOnboarding | null
  triggerEvents?: SkillTriggerEvents
  triggerEventScopes?: SkillTriggerEventScopes
  triggerEventsUserEdited?: boolean
  /**
   * When true, a webhook-triggered run approves its own actions on the skill's
   * trigger apps instead of staging them for the user. Those actions run as the
   * user. Has no effect without `triggerEvents`.
   */
  autoMode: boolean
  /** The model that runs this skill. Null means Auto, where the backend picks per run. */
  modelId?: string | null
}

/** One choice from `GET /api/v1/skills/models`, in display order. */
export interface SkillModel {
  id: string
  name: string
  provider: 'anthropic' | 'openai'
  priceTier: number
  isFree: boolean
  /** Inline SVG markup. */
  icon: string | null
}

export type ContextHistory = {
  window: {
    appName: string
    title: string
    base64Image: string
    url?: string
  }
}

// Mentions

export type MentionableTypes = 'shortcut' | 'backend_plugin' | 'model' | 'browser_tab'
type Mentionable = {
  label: string
  value: string
  type: MentionableTypes
}
export type MentionableShortcut = Mentionable & {
  type: 'shortcut'
  skillId?: string
  images?: {
    image: string | null
    userImages: UserImage | null
  }
  agent_tools: {
    [key: string]: { enabled: boolean; tools: string[] | '*' }
  } | null
}
export type MentionableBackendPlugin = Mentionable & {
  type: 'backend_plugin'
  images?: {
    image: string | null
  }
  isPro: boolean
}
export type MentionableBrowserTab = Mentionable & {
  type: 'browser_tab'
  id: number
  images?: {
    image: string | null
  }
}

export type MentionableModel = Mentionable & {
  type: 'model'
  id: string
  provider: string
  apiVersion?: string[]
  byok?: boolean
  custom_host?: string
  api_key?: string
  capabilities?: {
    mode?: string
    supportsFunctionCalling?: boolean
    supportsParallelFunctionCalling?: boolean
    supportsResponseSchema?: boolean
    supportsVision?: boolean
    supportsPromptCaching?: boolean
    supportsSystemMessages?: boolean
    supportsDeveloperMessages?: boolean
    supportsToolChoice?: boolean
    supportsStreaming?: boolean
    supportsThinking?: boolean
    /** A chat turn's `reasoningEffort` changes how this model reasons. */
    supportsReasoningEffort?: boolean
    supportsTemperature?: boolean
  }
  limits?: {
    maxTokens: number
    maxInputTokens: number
    maxOutputTokens: number
    maxImageSize?: number
    inputCostPerToken?: number
    outputCostPerToken?: number
    inputCostPerTokenBatches?: number
    outputCostPerTokenBatches?: number
    cacheReadInputTokenCost?: number
    knowledgeCutoffDate?: string
  }
  pricing?: {
    isFree: boolean
    priceTier: number
    subscriptionName: string
    subscriptionTier: string
    subscriptionId: string
    stripePriceIdProd: string | null
  }
  meta?: {
    public: boolean
    name: string
    displayName: string
    description: string
    icon: string | null
    costPriority?: number
    speedPriority?: number
    intelligencePriority?: number
    experimental?: boolean
    removeable?: boolean
    // Ids of now-deprecated models this model is served in place of.
    replaces?: Array<string>
  }
  images?: {
    image: string | null
  }
}
export type MentionableItem = MentionableShortcut | MentionableBackendPlugin | MentionableModel | MentionableBrowserTab

// Export everything!

export * from './attachments'
export * from './screenshots'
export * from './ui'
export * from './shortcuts'
export * from './context-types'
export * from './models'
export * from './usage'
export * from './summaries'
export * from './gateway'
